import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade,
  Chip,
  Pagination,
  Stack,
} from "@mui/material";
import {
  Add,
  Edit,
  Refresh,
  Assignment,
  ArrowBack,
} from "@mui/icons-material";
// Променете импортната секция: използвайте chitalishteAdminSlice вместо chitalishtaSlice
import {
  fetchInformationCardsByChitalishte,
  createInformationCard,
  updateInformationCard,
  deleteInformationCard,
  clearError,
  selectInformationCardsByChitalishteId,
  selectInformationCardsLoading,
  selectInformationCardsError,
} from "../../../store/slices/chitalishteAdminSlice"; // Това е важната промяна!
import InformationCardForm from "./InformationCardForm";

const InformationCardsManager = ({ chitalishte, onBack }) => {
  const dispatch = useDispatch();
  
  // Променете селектора: използвайте правилния селектор от chitalishteAdminSlice
  const informationCards = useSelector((state) =>
    selectInformationCardsByChitalishteId(chitalishte?.id)(state)
  );
  
  const cardsLoading = useSelector(selectInformationCardsLoading);
  const cardsError = useSelector(selectInformationCardsError);

  const [deleteFeedbackId, setDeleteFeedbackId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (chitalishte?.id) {
      // console.log("Fetching information cards for chitalishte:", chitalishte.id);
      dispatch(fetchInformationCardsByChitalishte(chitalishte.id));
    }
  }, [dispatch, chitalishte]);

  // КОРЕКЦИЯ: Правилно обработване на информация за картите
  const cardsArray = Array.isArray(informationCards) 
    ? informationCards 
    : informationCards?.content 
    ? informationCards.content 
    : informationCards?.data 
    ? informationCards.data 
    : [];

  // console.log("Cards array:", cardsArray);
  // console.log("Chitalishte:", chitalishte);

  const handleCreate = () => {
    setSelectedCard(null);
    setFormOpen(true);
  };

  const handleEdit = (card) => {
    setSelectedCard(card);
    setFormOpen(true);
  };

  const handleDelete = (card) => {
    setCardToDelete(card);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = (formData) => {
    // Extract feedbackId from formData
    const { feedbackId, ...cardData } = formData;

    // Only include feedbackId if it's provided and not empty
    const feedbackIdToSend =
      feedbackId && feedbackId.trim() ? feedbackId.trim() : null;

    const submitData = {
      ...cardData,
      chitalishteId: chitalishte.id,
    };

    if (selectedCard) {
      // Update existing information card
      dispatch(
        updateInformationCard({
          id: selectedCard.id,
          data: submitData,
          feedbackId: feedbackIdToSend,
        }),
      ).then(() => {
        setFormOpen(false);
        dispatch(fetchInformationCardsByChitalishte(chitalishte.id));
      });
    } else {
      // Create new information card
      dispatch(
        createInformationCard({
          data: submitData,
          feedbackId: feedbackIdToSend,
        }),
      ).then(() => {
        setFormOpen(false);
        dispatch(fetchInformationCardsByChitalishte(chitalishte.id));
      });
    }
  };

  const confirmDelete = () => {
    if (cardToDelete) {
      const feedbackIdToSend = deleteFeedbackId.trim() || null;
      dispatch(
        deleteInformationCard({
          id: cardToDelete.id,
          feedbackId: feedbackIdToSend,
        }),
      ).then(() => {
        setDeleteDialogOpen(false);
        setCardToDelete(null);
        setDeleteFeedbackId("");
        dispatch(fetchInformationCardsByChitalishte(chitalishte.id));
      });
    }
  };

  const handleRefresh = () => {
    if (chitalishte?.id) {
      dispatch(fetchInformationCardsByChitalishte(chitalishte.id));
    }
  };

  // Пагинация - използваме cardsArray
  const totalPages = Math.ceil(cardsArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCards = cardsArray.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  if (!chitalishte) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Няма избрано читалище</Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Назад
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{ mb: 2 }}
          variant="outlined"
        >
          Назад към читалищата
        </Button>

        <Paper
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Информационни карти
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            {chitalishte.name} - {chitalishte.town}, {chitalishte.municipality}
          </Typography>
        </Paper>
      </Box>

      {/* Error Alert */}
      {cardsError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {cardsError.message || cardsError}
        </Alert>
      )}

      {/* Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Управление на информационни карти ({cardsArray.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="outlined"
            disabled={!chitalishte?.id}
          >
            Обнови
          </Button>
          <Button
            startIcon={<Add />}
            onClick={handleCreate}
            variant="contained"
          >
            Добави Карта
          </Button>
        </Box>
      </Box>

      {/* Information Cards Table */}
      {cardsArray.length > 0 ? (
        <>
          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold", width: "100px" }}
                  >
                    Година
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold", width: "100px" }}
                  >
                    Членове
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold", width: "100px" }}
                  >
                    Служители
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold", width: "120px" }}
                  >
                    Формации
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold", width: "100px" }}
                  >
                    Проекти
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      width: "120px",
                      textAlign: "center",
                    }}
                  >
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCards.map((card) => (
                  <Fade in={true} key={card.id}>
                    <TableRow hover>
                      <TableCell>
                        <Chip
                          label={card.year}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {card.totalMembersCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>{card.employeesCount || 0}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={`Фолклорни: ${card.folkloreFormations || 0}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Театрални: ${card.theatreFormations || 0}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Танцови: ${card.dancingGroups || 0}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={`Самостоятелни: ${card.projectsParticipationLeading || 0}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Партньорски: ${card.projectsParticipationPartner || 0}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box
                          sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                        >
                          <Tooltip title="Редактирай">
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(card)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="Изтрий">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(card)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip> */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Пагинация */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </>
      ) : (
        /* Empty State */
        !cardsLoading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Assignment sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Няма информационни карти за това читалище
            </Typography>
            <Button
              startIcon={<Add />}
              onClick={handleCreate}
              variant="contained"
            >
              Добави първата карта
            </Button>
          </Box>
        )
      )}

      {/* Loading State */}
      {cardsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Forms and Dialogs */}
      <InformationCardForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        card={selectedCard}
        chitalishte={chitalishte}
        onSubmit={handleSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteFeedbackId("");
        }}
      >
        <DialogTitle color="error">Потвърждение за изтриване</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Сигурни ли сте, че искате да изтриете информационната карта за{" "}
            {cardToDelete?.year} година? Това действие е необратимо.
          </Typography>

          <TextField
            fullWidth
            label="Feedback ID (опционално)"
            value={deleteFeedbackId}
            onChange={(e) => setDeleteFeedbackId(e.target.value)}
            helperText="Свържи изтриването с конкретен feedback (UUID формат)"
            placeholder="напр. 123e4567-e89b-12d3-a456-426614174000"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteFeedbackId("");
            }}
          >
            Отказ
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Изтрий
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InformationCardsManager;